import type {
  DomainClassSummary,
  DomainMethodIR,
  ManifestStateMachineInput,
  MetamodelGraphqlMutation,
  DomainStateMachineSummary,
  DomainTypeIR,
} from "@granular-software/metamodel-core";
import {
  defineMetamodelPackage,
  quoteTsStringLiteral,
  toPascalCase,
} from "@granular-software/metamodel-core";

function normalizeStateMachines(
  values: Array<Record<string, any>> | null | undefined,
): DomainStateMachineSummary[] {
  return (values || [])
    .map((machine) => {
      const states = (machine?.states || [])
        .map((state: Record<string, any>) => ({
          name: String(state?.name || ""),
          isFinal: Boolean(state?.is_final),
        }))
        .filter((state: { name: string }) => state.name.length > 0);

      const transitions = (machine?.transitions || [])
        .map((transition: Record<string, any>) => ({
          name: String(transition?.name || ""),
          from: String(transition?.from?.name || ""),
          to: String(transition?.to?.name || ""),
        }))
        .filter(
          (transition: { name: string; from: string; to: string }) =>
            transition.name.length > 0 &&
            transition.from.length > 0 &&
            transition.to.length > 0,
        );

      return {
        name: String(machine?.name || ""),
        entryState:
          typeof machine?.entry_state?.name === "string"
            ? machine.entry_state.name
            : null,
        finalStates: states
          .filter((state: { isFinal: boolean }) => state.isFinal)
          .map((state: { name: string }) => state.name),
        states,
        transitions,
      } satisfies DomainStateMachineSummary;
    })
    .filter((machine) => machine.name.length > 0);
}

function stateTypeName(className: string, machineName: string) {
  return `${toPascalCase(className)}${toPascalCase(machineName)}`;
}

function transitionTypeName(className: string, machineName: string) {
  return `${stateTypeName(className, machineName)}Transition`;
}

function pathTypeName(className: string, machineName: string) {
  return `${stateTypeName(className, machineName)}Path`;
}

function normalizeStateDefinitions(machine: ManifestStateMachineInput) {
  const finalStates = new Set(machine.finalStates || []);
  const states = new Map<string, { name: string; isFinal: boolean }>();

  for (const rawState of machine.states || []) {
    if (typeof rawState === "string") {
      states.set(rawState, {
        name: rawState,
        isFinal: finalStates.has(rawState),
      });
      continue;
    }

    states.set(rawState.name, {
      name: rawState.name,
      isFinal: Boolean(rawState.isFinal) || finalStates.has(rawState.name),
    });
  }

  if (!states.has(machine.entryState)) {
    states.set(machine.entryState, {
      name: machine.entryState,
      isFinal: finalStates.has(machine.entryState),
    });
  }

  return [...states.values()];
}

export function buildStateMachineModelMutations(
  modelPath: string,
  machines?: ManifestStateMachineInput[],
): MetamodelGraphqlMutation[] {
  const mutations: MetamodelGraphqlMutation[] = [];

  for (const machine of machines || []) {
    mutations.push({
      label: `create state machine ${modelPath}.${machine.name}`,
      query: `mutation { at(path: ${JSON.stringify(modelPath)}) { create_state_machine(name: ${JSON.stringify(
        machine.name,
      )}, entry_state: ${JSON.stringify(machine.entryState)}) { name } } }`,
    });

    for (const state of normalizeStateDefinitions(machine)) {
      if (state.name === machine.entryState && !state.isFinal) continue;
      mutations.push({
        label: `add state ${state.name} on ${modelPath}.${machine.name}`,
        query: `mutation { at(path: ${JSON.stringify(modelPath)}) { state_machine(name: ${JSON.stringify(
          machine.name,
        )}) { add_state(name: ${JSON.stringify(state.name)}, is_final: ${state.isFinal}) { name } } } }`,
      });
    }

    for (const transition of machine.transitions || []) {
      mutations.push({
        label: `add transition ${transition.name} on ${modelPath}.${machine.name}`,
        query: `mutation { at(path: ${JSON.stringify(modelPath)}) { state_machine(name: ${JSON.stringify(
          machine.name,
        )}) { add_transition(name: ${JSON.stringify(
          transition.name,
        )}, from: ${JSON.stringify(transition.from)}, to: ${JSON.stringify(
          transition.to,
        )}) { name } } } }`,
      });
    }
  }

  return mutations;
}

function buildMachineTypes(
  classSummary: DomainClassSummary,
  machine: DomainStateMachineSummary,
): DomainTypeIR[] {
  return [
    {
      kind: "union",
      name: stateTypeName(classSummary.name, machine.name),
      docs: [`Allowed states for ${classSummary.name}.${machine.name}.`],
      members: machine.states.map((state) => state.name),
    },
    {
      kind: "union",
      name: transitionTypeName(classSummary.name, machine.name),
      docs: [`Allowed transitions for ${classSummary.name}.${machine.name}.`],
      members: machine.transitions.map((transition) => transition.name),
    },
  ];
}

function buildMachineMethods(
  classSummary: DomainClassSummary,
  machine: DomainStateMachineSummary,
): DomainMethodIR[] {
  const stateName = stateTypeName(classSummary.name, machine.name);
  const transitionName = transitionTypeName(classSummary.name, machine.name);
  const pathName = pathTypeName(classSummary.name, machine.name);

  const docsPrefix = `${classSummary.name}.${machine.name}`;

  return [
    {
      name: `get_${machine.name}`,
      docs: [`Get the current ${docsPrefix} state.`],
      static: false,
      params: [],
      returnType: `Promise<${stateName} | null>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "get_current",
      },
    },
    {
      name: `reach_${machine.name}`,
      docs: [
        `Reach a ${docsPrefix} state through the shortest allowed transition path.`,
      ],
      static: false,
      params: [{ name: "target", type: stateName }],
      returnType: `Promise<${toPascalCase(classSummary.name)}>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "reach",
      },
    },
    {
      name: `list_${machine.name}_transitions`,
      docs: [`List currently available transitions for ${docsPrefix}.`],
      static: false,
      params: [],
      returnType: `Promise<${transitionName}[]>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "list_transitions",
      },
    },
    {
      name: `list_${machine.name}_reachable_states`,
      docs: [`List reachable states for ${docsPrefix} from the current state.`],
      static: false,
      params: [],
      returnType: `Promise<${stateName}[]>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "list_reachable_states",
      },
    },
    {
      name: `${machine.name}_is_final`,
      docs: [`Return whether the current ${docsPrefix} state is final.`],
      static: false,
      params: [],
      returnType: "Promise<boolean>",
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "is_final",
      },
    },
    {
      name: `paths_to_${machine.name}`,
      docs: [
        `List shortest transition paths from the current ${docsPrefix} state to a target state.`,
      ],
      static: false,
      params: [{ name: "target", type: stateName }],
      returnType: `Promise<Array<{ states: ${stateName}[]; transitions: ${transitionName}[] }>>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "paths_to",
      },
    },
  ];
}

export function readStateMachineSummaries(
  rawClass: Record<string, any>,
): Partial<DomainClassSummary> {
  return {
    stateMachines: normalizeStateMachines(rawClass.states),
  };
}

export const stateMachineMetamodelPackage = defineMetamodelPackage({
  id: "state_machine",
  docs: {
    modelRows: [
      {
        key: "stateMachines",
        description:
          "State machine definitions with `entryState`, `states`, and `transitions`.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type StateMachineMutation {
        name: String!
        state_machine: StateMachine!
        add_state(name: String!, is_final: Boolean): StateMachineMutation!
        add_transition(name: String!, from: String!, to: String!): StateMachineMutation!
        activate_transition(name: String!): StateMachineMutation!
      }

      type StateMachineSnapshot {
        name: String!
        current_state: StateMachineState
        available_transitions: [StateMachineTransition!]!
        reachable_states: [StateMachineState!]!
        paths_to(state: String!): [StateMachinePath!]!
        is_final: Boolean!
        history: [StateMachineTransitionEvent!]!
      }

      type StateMachineSnapshotMutation {
        snapshot: StateMachineSnapshot!
        activate_transition(name: String!): StateMachineSnapshotMutation!
      }

      type StateMachine {
        name: String!
        model: Model!
        entry_state: StateMachineState
        states: [StateMachineState!]!
        transitions: [StateMachineTransition!]!
        current_state: StateMachineState
        available_transitions: [StateMachineTransition!]!
        reachable_states: [StateMachineState!]!
        paths_to(state: String!): [StateMachinePath!]!
        is_final: Boolean!
        history: [StateMachineTransitionEvent!]!
        instances_in_state(state: String!): [Model!]!
      }

      type StateMachineState {
        name: String!
        is_final: Boolean!
      }

      type StateMachineTransition {
        name: String!
        from: StateMachineState!
        to: StateMachineState!
      }

      type StateMachinePath {
        states: [StateMachineState!]!
        transitions: [StateMachineTransition!]!
      }

      type StateMachineTransitionEvent {
        sequence: Int!
        occurred_at: Float!
        transition: StateMachineTransition!
        from: StateMachineState!
        to: StateMachineState!
      }

      extend type Model {
        state(name: String!): StateMachineSnapshot
        states: [StateMachine!]!
        state_machine(name: String!): StateMachine
      }

      extend type ModelMutation {
        create_state_machine(name: String!, entry_state: String!): StateMachineMutation
        state(name: String!): StateMachineSnapshotMutation
        state_machine(name: String!): StateMachineMutation
      }
      `,
    ],
    createResolvers({ run, stateMachines }) {
      return {
        Model: {
          state: async (ant, { name }) => await run(ant.state_machine(name)),
          states: async (ant) => await run(ant.state_machines()),
          state_machine: async (ant, { name }) =>
            await run(ant.state_machine(name)),
        },
        ModelMutation: {
          create_state_machine: async (ant, { name, entry_state }) => {
            await run(ant.create_state_machine(name, entry_state));
            return { target: ant, name };
          },
          state: async (ant, { name }) => {
            const machine = await run(ant.state_machine(name));
            return machine ? { target: ant, name } : null;
          },
          state_machine: async (ant, { name }) => {
            const machine = await run(ant.state_machine(name));
            return machine ? { target: ant, name } : null;
          },
        },
        StateMachineMutation: {
          name: (value) => value.name,
          state_machine: async (value) =>
            await run(value.target.state_machine(value.name)),
          add_state: async (value, { name, is_final }) => {
            await run(
              value.target.add_state_machine_state(
                value.name,
                name,
                is_final ?? false,
              ),
            );
            return value;
          },
          add_transition: async (value, { name, from, to }) => {
            await run(
              value.target.add_state_machine_transition(
                value.name,
                name,
                from,
                to,
              ),
            );
            return value;
          },
          activate_transition: async (value, { name }) => {
            await run(
              value.target.activate_state_machine_transition(value.name, name),
            );
            return value;
          },
        },
        StateMachineSnapshotMutation: {
          snapshot: async (value) =>
            await run(value.target.state_machine(value.name)),
          activate_transition: async (value, { name }) => {
            await run(
              value.target.activate_state_machine_transition(value.name, name),
            );
            return value;
          },
        },
        StateMachineState: {
          name: (value) => value.name,
          is_final: (value) => value.is_final,
        },
        StateMachineTransition: {
          name: (value) => value.name,
          from: (value) =>
            value.from_state || { name: value.from, is_final: false },
          to: (value) => value.to_state || { name: value.to, is_final: false },
        },
        StateMachinePath: {
          states: (value) => value.states,
          transitions: (value) => value.transitions,
        },
        StateMachineTransitionEvent: {
          sequence: (value) => value.sequence,
          occurred_at: (value) => value.occurred_at,
          transition: (value) => value.transition,
          from: (value) => value.from,
          to: (value) => value.to,
        },
        StateMachineSnapshot: {
          name: (value) => value.name,
          current_state: (value) => value.current_state,
          available_transitions: (value) => value.available_transitions,
          reachable_states: (value) => value.reachable_states,
          is_final: (value) => value.is_final,
          history: (value) => value.history,
          paths_to: async (value, { state }) =>
            await stateMachines!.pathsToState(
              value.model.target || value.model,
              value.name,
              state,
            ),
        },
        StateMachine: {
          name: (value) => value.name,
          model: (value) => value.model,
          entry_state: (value) => value.entry_state,
          states: (value) => value.states,
          transitions: (value) => value.transitions,
          current_state: (value) => value.current_state,
          available_transitions: (value) => value.available_transitions,
          reachable_states: (value) => value.reachable_states,
          is_final: (value) => value.is_final,
          history: (value) => value.history,
          paths_to: async (value, { state }) =>
            await stateMachines!.pathsToState(
              value.model.target || value.model,
              value.name,
              state,
            ),
          instances_in_state: async (value, { state }) =>
            await stateMachines!.instancesInState(
              value.model.target || value.model,
              value.name,
              state,
            ),
        },
      };
    },
  },
  manifest: {
    buildModelMutations(modelPath, spec) {
      return buildStateMachineModelMutations(modelPath, spec.stateMachines);
    },
  },
  summary: {
    selections: {
      classFields: [
        `states {
          name
          entry_state { name }
          states { name is_final }
          transitions {
            name
            from { name }
            to { name }
          }
        }`,
      ],
    },
    readClassSummary: readStateMachineSummaries,
  },
  domain: {
    extraTypesForClass(classSummary) {
      return classSummary.stateMachines.flatMap((machine) =>
        buildMachineTypes(classSummary, machine),
      );
    },
    extraMethodsForClass(classSummary) {
      return classSummary.stateMachines.flatMap((machine) =>
        buildMachineMethods(classSummary, machine),
      );
    },
  },
});

export function renderUnionType(typeName: string, members: string[]) {
  if (members.length === 0) return `export type ${typeName} = never;`;
  return `export type ${typeName} = ${members.map(quoteTsStringLiteral).join(" | ")};`;
}
